import api from '../utils/api';

const API_URL = '/scripts';

export const createBlankScript = async ({ projectId, title, content }) => {
  try {
    if (!projectId) {
      throw new Error('Project ID is required');
    }
    if (!title || !title.trim()) {
      throw new Error('Title is required');
    }

    const response = await api.post(`${API_URL}/blank`, {
      projectId,
      title: title.trim(),
      content
    });

    return response.data;
  } catch (error) {
    console.error('Error creating blank script:', error);
    throw error.response?.data?.message || error.message || 'Failed to create script';
  }
};

export const uploadScript = async (formData) => {
  try {
    console.log('=== Start Script Upload ===');
    console.log('FormData contents:', {
      title: formData.get('title'),
      projectId: formData.get('projectId'),
      hasFile: formData.has('scriptFile'),
      fileName: formData.get('scriptFile')?.name,
      fileType: formData.get('scriptFile')?.type,
      fileSize: formData.get('scriptFile')?.size
    });

    // Log all form data entries
    console.log('All FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value instanceof File ? {
        name: value.name,
        type: value.type,
        size: value.size
      } : value);
    }

    console.log('Making upload request...');
    const response = await api.post(API_URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      validateStatus: false,
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log('Upload progress:', percentCompleted + '%');
      }
    });

    console.log('Upload response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      headers: response.headers
    });

    if (response.status !== 201) {
      console.error('Upload failed with status:', response.status);
      throw new Error(response.data?.message || `Upload failed with status ${response.status}`);
    }

    console.log('Upload successful!');
    console.log('=== End Script Upload ===');
    return response.data;
  } catch (error) {
    console.error('Upload error:', {
      message: error.message,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      } : undefined,
      request: error.request ? 'Present' : 'Missing'
    });
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Error uploading script');
    }
  }
};

export const getScriptContent = async (scriptId, version = 'latest', projectId) => {
  try {
    // First try to get the content type
    const headResponse = await api.head(`${API_URL}/${scriptId}/content`, {
      params: { version, projectId }
    });
    
    const contentType = headResponse.headers['content-type'];
    console.log('Content type:', contentType);

    // For PDF files, get a blob
    if (contentType?.includes('application/pdf')) {
      const response = await api.get(`${API_URL}/${scriptId}/content`, {
        params: { version, projectId },
        responseType: 'blob'
      });

      return {
        type: 'pdf',
        url: URL.createObjectURL(response.data)
      };
    }

    // For text content
    const response = await api.get(`${API_URL}/${scriptId}/content`, {
      params: { version, projectId }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching script content:', error);
    throw error;
  }
};

export const getProjectScripts = async (projectId) => {
  try {
    console.log('Fetching project scripts:', { projectId });
    const response = await api.get(`${API_URL}/project/${projectId}`, {
      validateStatus: false
    });

    if (response.status !== 200) {
      throw new Error(response.data?.message || `Failed to fetch scripts with status ${response.status}`);
    }

    // Add projectId to each script object
    const scripts = response.data.map(script => ({
      ...script,
      projectId
    }));

    return scripts;
  } catch (error) {
    console.error('Error fetching project scripts:', error);
    throw error.response?.data?.message || error.message || 'Error fetching project scripts';
  }
};

export const uploadNewVersion = async (scriptId, formData) => {
  try {
    console.log('Preparing to upload new version:', {
      scriptId,
      hasFile: formData.has('scriptFile'),
      fileName: formData.get('scriptFile')?.name
    });

    const response = await api.post(`${API_URL}/${scriptId}/versions`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      validateStatus: false
    });

    console.log('New version response:', {
      status: response.status,
      data: response.data
    });

    if (response.status !== 200 && response.status !== 201) {
      throw new Error(response.data?.message || `Upload failed with status ${response.status}`);
    }

    return response.data;
  } catch (error) {
    console.error('New version upload error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error.response?.data?.message || error.message || 'Error uploading new version';
  }
};

export const deleteScript = async (scriptId, projectId) => {
  try {
    const response = await api.delete(`${API_URL}/${scriptId}`, {
      params: {
        projectId
      }
    });

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to delete script');
    }

    return response.data;
  } catch (error) {
    console.error('Error deleting script:', error);
    throw error.response?.data?.message || error.message || 'Error deleting script';
  }
};

export const updateScriptTags = async (scriptId, projectId, tags) => {
  try {
    console.log('Updating script tags:', { scriptId, projectId, tagsCount: tags.length });
    
    // Ensure all tags have the required fields
    // IMPORTANT: Preserve scene number and scene ID to maintain associations after reordering
    const validatedTags = tags.map(tag => ({
      id: tag.id || `tag_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: tag.type || 'note',
      text: tag.text || '',
      lineNumber: tag.lineNumber || 0,
      color: tag.color || '#ffeb3b',
      // Preserve scene identifiers for stable matching after scene reordering
      sceneId: tag.sceneId || null,
      sceneNumber: tag.sceneNumber || null,
      number: tag.number || null,
      scene: tag.scene || null
    }));
    
    const response = await api.put(`${API_URL}/${scriptId}/tags`, {
      projectId,
      tags: validatedTags
    });
    
    console.log('Tags updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating script tags:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      scriptId,
      projectId,
      tagsCount: tags?.length
    });
    throw error;
  }
};

export const autoTagScript = async (scriptId, projectId) => {
  console.log('Calling autoTagScript service...', { scriptId, projectId });
  try {
    const response = await api.post(`${API_URL}/${scriptId}/auto-tag`, {
      projectId
    });
    console.log('Auto-tag service response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error in autoTagScript service:', error.response?.data || error);
    throw error;
  }
};
